// Situação do horário de um departamento, como o backend calcula.
//
// O backend usa o relógio de -03:00 (o fuso fixo do banco), não o do
// navegador, e só aplica os horários quando a empresa está no modo
// "Por departamento" e o departamento tem mensagem de fora do expediente.
// Quem chama decide sobre o modo; aqui entra a parte do departamento.

export const SERVER_OFFSET_MIN = -180;

const DAYS_EN = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
const DAYS_SHORT = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];

// Dia com restrição: fechado, ou com início e fim preenchidos. Dia sem
// restrição não limita nada (o departamento atende o dia todo).
const restrictedDay = (schedules, weekdayEn) =>
  (schedules || []).find(
    (s) =>
      s.weekdayEn === weekdayEn &&
      (s.closed === true || (s.startTime && s.endTime))
  );

const toSeconds = (hhmm) => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm || ""));
  return m ? Number(m[1]) * 3600 + Number(m[2]) * 60 : null;
};

// Data "de parede" no fuso do servidor, lida com getUTC*.
const serverClock = (now) => new Date(now.getTime() + SERVER_OFFSET_MIN * 60000);

const nextOpening = (schedules, clock) => {
  const today = clock.getUTCDay();
  const secs = clock.getUTCHours() * 3600 + clock.getUTCMinutes() * 60 + clock.getUTCSeconds();

  const todaySched = restrictedDay(schedules, DAYS_EN[today]);
  if (todaySched && !todaySched.closed) {
    const start = toSeconds(todaySched.startTime);
    if (start !== null && secs < start) return `abre ${todaySched.startTime}`;
  }

  for (let i = 1; i <= 7; i += 1) {
    const day = (today + i) % 7;
    const label = i === 1 ? "amanhã" : DAYS_SHORT[day];
    const s = restrictedDay(schedules, DAYS_EN[day]);
    if (!s) return `abre ${label}`;
    if (s.closed) continue;
    return `abre ${label} ${s.startTime}`;
  }
  return null;
};

// Retorna null quando o horário não se aplica, ou { open, label }.
export const queueHoursStatus = (queue, now = new Date()) => {
  if (!queue || !String(queue.outOfHoursMessage || "").trim()) return null;
  const schedules = Array.isArray(queue.schedules) ? queue.schedules : [];
  if (schedules.length === 0) return null;

  const clock = serverClock(now);
  const today = restrictedDay(schedules, DAYS_EN[clock.getUTCDay()]);
  if (!today) return null;

  let open = false;
  if (!today.closed) {
    const start = toSeconds(today.startTime);
    const end = toSeconds(today.endTime);
    // Horário ilegível nunca fechou o departamento no backend.
    if (start === null || end === null) return null;
    const secs = clock.getUTCHours() * 3600 + clock.getUTCMinutes() * 60 + clock.getUTCSeconds();
    open = secs >= start && secs <= end;
  }

  if (open) return { open: true, label: `Aberto até ${today.endTime}` };
  const next = nextOpening(schedules, clock);
  return { open: false, label: next ? `Fechado · ${next}` : "Fechado" };
};

// Ordem do menu: pela posição salva; sem posição vai para o fim.
export const sortQueues = (queues) =>
  [...queues].sort((a, b) => {
    const oa = a.orderQueue == null || a.orderQueue === "" ? Infinity : Number(a.orderQueue);
    const ob = b.orderQueue == null || b.orderQueue === "" ? Infinity : Number(b.orderQueue);
    if (oa !== ob) return oa - ob;
    return a.id - b.id;
  });

// Move um departamento uma posição e devolve só os que mudaram de número,
// já renumerados de 1 a N (corrige posições vazias ou repetidas).
export const moveQueue = (queues, queueId, direction) => {
  const list = sortQueues(queues);
  const from = list.findIndex((q) => q.id === queueId);
  const to = from + direction;
  if (from === -1 || to < 0 || to >= list.length) return [];
  [list[from], list[to]] = [list[to], list[from]];
  return list
    .map((q, i) => ({ queue: q, orderQueue: i + 1 }))
    .filter(({ queue, orderQueue }) => Number(queue.orderQueue) !== orderQueue);
};
